import { HierarchyNode } from 'core/API/hierarchy';

/**
 * Gets the current user's information from localStorage
 */
export const getCurrentUser = () => {
	try {
		const storedData = localStorage.getItem('BYOD-app');
		if (storedData) {
			const parsedData = JSON.parse(storedData);
			return parsedData?.user || null;
		}
	} catch (error) {
		console.error('Error getting current user from localStorage:', error);
	}
	return null;
};

/**
 * Builds a map of hierarchy nodes by their ID for quick lookups
 */
const buildHierarchyMap = (nodes: HierarchyNode[]): Map<number, HierarchyNode> => {
	return new Map(nodes.map(node => [node.id, node]));
};

/**
 * Gets all ancestor IDs of a hierarchy node (including itself)
 * @param nodeId - The ID of the hierarchy node
 * @param hierarchyNodes - All hierarchy nodes
 * @returns Array of ancestor IDs from root to the node itself
 */
export const getAncestorIds = (nodeId: number, hierarchyNodes: HierarchyNode[]): number[] => {
	const hierarchyMap = buildHierarchyMap(hierarchyNodes);
	const ancestors: number[] = [];
	let currentId: number | null = nodeId;
	const visited = new Set<number>();

	while (currentId !== null) {
		// Prevent infinite loops
		if (visited.has(currentId)) {
			console.warn('Circular reference detected in hierarchy');
			break;
		}
		visited.add(currentId);

		const node = hierarchyMap.get(currentId);
		if (!node) break;

		ancestors.unshift(currentId);
		currentId = node.parentId;
	}

	return ancestors;
};

/**
 * Gets the depth/level of a hierarchy node (0 = organization/root)
 * @param nodeId - The ID of the hierarchy node
 * @param hierarchyNodes - All hierarchy nodes
 * @returns The depth of the node in the tree
 */
export const getNodeDepth = (nodeId: number, hierarchyNodes: HierarchyNode[]): number => {
	const ancestors = getAncestorIds(nodeId, hierarchyNodes);
	return ancestors.length - 1;
};

/**
 * Checks if a user with hierarchy X can initiate onboarding for a user with hierarchy Y
 * Rule: User can initiate if Y is the same as X OR Y is a descendant (child) of X
 * 
 * @param userHierarchyId - The ID of the initiating user's hierarchy
 * @param targetHierarchyId - The ID of the target user's hierarchy
 * @param hierarchyNodes - All hierarchy nodes
 * @returns true if user can initiate onboarding for the target hierarchy
 */
export const canInitiateForHierarchy = (
	userHierarchyId: number,
	targetHierarchyId: number,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	// User can always initiate for the same hierarchy
	if (userHierarchyId === targetHierarchyId) {
		return true;
	}

	const hierarchyMap = buildHierarchyMap(hierarchyNodes);
	const userNode = hierarchyMap.get(userHierarchyId);
	const targetNode = hierarchyMap.get(targetHierarchyId);

	// If nodes don't exist, deny access
	if (!userNode || !targetNode) {
		console.warn('⚠️ Node not found:', { userHierarchyId, targetHierarchyId, userNode: !!userNode, targetNode: !!targetNode });
		return false;
	}

	// Get ancestors of the target node
	const targetAncestors = getAncestorIds(targetHierarchyId, hierarchyNodes);

	// Check if the user's hierarchy ID is in the target's ancestor chain
	// If it is, then target is a descendant of user, so user can initiate
	const isDescendant = targetAncestors.includes(userHierarchyId);
	
	console.log('🔐 Permission Check:', {
		userHierarchyId,
		targetHierarchyId,
		targetAncestors,
		isDescendant,
		canInitiate: isDescendant
	});
	
	return isDescendant;
};

/**
 * Filters hierarchy nodes to only include those that the current user can initiate onboarding for
 * @param currentUserHierarchyId - The ID of the current user's hierarchy
 * @param allHierarchyNodes - All available hierarchy nodes
 * @returns Filtered hierarchy nodes that the user can initiate for
 */
export const getPermittedHierarchies = (
	currentUserHierarchyId: number,
	allHierarchyNodes: HierarchyNode[]
): HierarchyNode[] => {
	return allHierarchyNodes.filter(node => 
		canInitiateForHierarchy(currentUserHierarchyId, node.id, allHierarchyNodes)
	);
};

/**
 * Gets the current user's hierarchy ID from localStorage
 * @returns The hierarchy ID or null if not found
 */
export const getCurrentUserHierarchyId = (): number | null => {
	try {
		const currentUser = getCurrentUser();
		console.log('📊 Current User Data:', {
			user: currentUser,
			hasHierarchy: !!currentUser?.hierarchy,
			hierarchyId: currentUser?.hierarchy?.id
		});
		
		if (currentUser?.hierarchy?.id) {
			return currentUser.hierarchy.id;
		}
	} catch (error) {
		console.error('Error getting current user hierarchy:', error);
	}
	return null;
};
